import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import useScrollToElement from '../../../../hooks/dom/useScrollToElement';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import UIStore from '../../../../store/UIStore';
import PickupPointList from '../../common/PickupPointList';
import FormFieldId from '../../../../types/FormFieldId';
import { createFormField } from '../../../../models/FormField';
import safeCloneDeep from '../../../../util/safeCloneDeep';
import {
  isEmail,
  maxLength,
  minLength,
  notBlank,
} from '../../../../util/formValidators';
import FormFieldType from '../../../../types/FormFieldType';
import { labels } from '../../../../util/formLabels';
import { createForm } from '../../../../models/Form';
import CheckoutShippingCustomerForm from '../CheckoutShippingCustomerForm';
import CheckoutOrderExtrasForm from '../../recipient/CheckoutOrderExtrasForm';
import CheckoutShippingSelectionFieldsForm from '../CheckoutShippingSelectionFieldsForm';

const CheckoutShippingMethodContent = ({
  checkoutStore,
  uiStore,
  onOrderUpdate,
}) => {
  const { order } = checkoutStore;
  const {
    scroll_to_method,
    selected_shipping,
    shouldUseProviderCustomerHandling,
  } = order;
  const module = selected_shipping.module;
  const id = `CheckoutShippingMethodContent-${module.id}`.toLowerCase();
  const [ref] = useScrollToElement({
    elementIdToScroll: scroll_to_method,
    behavior: 'smooth',
  });
  // Extra offset: content is inside the radio field set, so we need header + buffer + space for the radio list above
  const RADIO_FIELDSET_OFFSET = 56;
  const MOBILE_EXTRA_OFFSET = 32;
  const extraOffset =
    RADIO_FIELDSET_OFFSET + (uiStore?.isMobile ? MOBILE_EXTRA_OFFSET : 0);
  const scrollMarginTop = Math.max(
    50 + extraOffset,
    (uiStore?.headerHeight ?? 50) + 8 + extraOffset
  );

  const form = useMemo(() => {
    const addressFormFields = {};

    // TODO hookkiin
    const getValidators = (formField, formLabels) => {
      const validators = [];

      if (formField.required) {
        validators.push(notBlank(formLabels[formField.id]));
      }

      if (formField.min_length) {
        validators.push(
          minLength(formLabels[formField.id], formField.min_length)
        );
      }

      if (formField.max_length) {
        validators.push(
          maxLength(formLabels[formField.id], formField.max_length)
        );
      }

      if (formField.type === FormFieldType.EMAIL) {
        validators.push(isEmail(labels.email));
      }

      return validators;
    };

    order.shippingAddressFields.forEach((formField) => {
      const validators = getValidators(formField, addressFormFields);
      addressFormFields[formField.id] = createFormField(
        safeCloneDeep(formField),
        validators
      );
    });

    const addressForm = createForm(addressFormFields);

    return { addressForm };
  }, [
    order.shippingAddressFields.length,
    order.getCustomerField(FormFieldId.DELIVERY_POSTAL_CODE).value,
    order.getCustomerField(FormFieldId.DELIVERY_STREET_ADDRESS).value,
  ]);

  return (
    <div
      ref={ref}
      className="CheckoutShippingMethodContent"
      id={id}
      style={{ scrollMarginTop }}
    >
      <p
        className="CheckoutShippingMethodContent__description"
        dangerouslySetInnerHTML={{
          __html: module.description,
        }}
      />
      {order.shippingAddressFields.length > 0 && (
        <>
          {module.requires_pickup_point && (
            <>
              <div className="CheckoutShippingMethodContent__deliveryAddressInfo">
                <FormattedMessage
                  id="checkout.shipping.deliveryAddressInfo"
                  defaultMessage="Please fill in the delivery address information below to be able to choose a pickup point"
                />
              </div>
              <CheckoutShippingCustomerForm
                onOrderUpdate={onOrderUpdate}
                addressForm={form.addressForm}
              />
            </>
          )}
        </>
      )}
      {module.requires_pickup_point && (
        <PickupPointList onOrderUpdate={onOrderUpdate} />
      )}
      {shouldUseProviderCustomerHandling && order.extraFields.length > 0 && (
        <CheckoutOrderExtrasForm onOrderUpdate={onOrderUpdate} />
      )}
      {module?.selectionFields && module.selectionFields.length > 0 && (
        <CheckoutShippingSelectionFieldsForm onOrderUpdate={onOrderUpdate} />
      )}
    </div>
  );
};

CheckoutShippingMethodContent.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  uiStore: modelOf(UIStore),
  onOrderUpdate: PropTypes.func.isRequired,
};

export default inject(
  'checkoutStore',
  'uiStore'
)(observer(CheckoutShippingMethodContent));
